import {
    ShoppingCart as CartIcon,
    DarkMode as DarkModeIcon,
    Email as EmailIcon,
    Facebook as FacebookIcon,
    Favorite as FavoriteIcon,
    Instagram as InstagramIcon,
    LightMode as LightModeIcon,
    LinkedIn as LinkedInIcon,
    LocationOn as LocationIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
    Twitter as TwitterIcon,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Button,
    Container,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { categories } from '../data/products';
import Logo from './Logo';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { state } = useCart();
  const { state: favoritesState } = useFavorites();
  const { user, signOut } = useAuth();
  const { theme: appTheme, toggleTheme } = useAppTheme();

  const pages = [
    { name: t.navigation.home, path: '/' },
    { name: t.navigation.products, path: '/products' },
    { name: t.navigation.about, path: '/about' },
    { name: t.navigation.contact, path: '/contact' },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      handleCloseUserMenu();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/products?category=${categoryId}`);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Logo size="medium" showTagline={true} />
      </Box>
      
      <List>
        {pages.map((page) => (
          <ListItem key={page.name} disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to={page.path}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary={page.name} />
            </ListItemButton>
          </ListItem>
        ))}
        {user && (
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/favorites"
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>
                <FavoriteIcon />
              </ListItemIcon>
              <ListItemText primary={`Mes Favoris (${favoritesState.itemCount})`} />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />
      
      <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
        {t.footer.categories}
      </Typography>
      
      <List>
        {categories.map((category) => (
          <ListItem key={category.id} disablePadding>
            <ListItemButton onClick={() => handleCategoryClick(category.id)}>
              <ListItemIcon>
                <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
              </ListItemIcon>
              <ListItemText 
                primary={category.nameFr}
                secondary={category.descriptionFr}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />
      
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t.footer.contactUs}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PhoneIcon sx={{ mr: 1, fontSize: 'small' }} />
          <Typography variant="body2">+221 77 123 4567</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EmailIcon sx={{ mr: 1, fontSize: 'small' }} />
          <Typography variant="body2">info@totalbat.com</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1, fontSize: 'small' }} />
          <Typography variant="body2">Dakar, Sénégal</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          zIndex: 1100,
          backgroundColor: (th) => alpha(th.palette.background.paper, th.palette.mode === 'dark' ? 0.6 : 0.75),
          color: 'text.primary',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: (th) => `0 6px 20px ${alpha(th.palette.common.black, 0.06)}`,
          '&:after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: (th) => `linear-gradient(90deg, ${alpha(th.palette.primary.main, 0)}, ${alpha(th.palette.primary.main, 0.3)} 25%, ${alpha(th.palette.secondary.main, 0.3)} 75%, ${alpha(th.palette.secondary.main, 0)})`,
          },
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters variant="dense" sx={{ justifyContent: 'space-between', minHeight: 44, py: 0 }}>
            {/* Left section */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, '& img': { height: 24, width: 'auto' } }}>
                <Logo size="small" showTagline={false} />
              </Box>

              {/* Desktop Navigation */}
              {!isMobile && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {pages.map((page) => (
                    <Button
                      key={page.name}
                      component={RouterLink}
                      to={page.path}
                      sx={{ 
                        borderRadius: 999,
                        px: 1.5,
                        py: 0.5,
                        fontWeight: location.pathname === page.path ? 700 : 500,
                        color: (th) => location.pathname === page.path ? th.palette.primary.main : th.palette.text.primary,
                        backgroundColor: (th) => location.pathname === page.path ? alpha(th.palette.primary.main, 0.12) : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: (th) => alpha(th.palette.action.hover, 0.6) }
                      }}
                    >
                      {page.name}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>

            {/* Right section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.75 } }}>
              {/* Theme Toggle - Hidden on very small screens */}
              <Tooltip title={appTheme === 'light' ? 'Mode sombre' : 'Mode clair'}>
                <IconButton 
                  color="inherit" 
                  size="small"
                  onClick={toggleTheme}
                  sx={{ display: { xs: 'none', sm: 'flex' }, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.06)' } }}
                >
                  {appTheme === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              {/* Search - Hidden on mobile */}
              <IconButton 
                color="inherit"
                size="small"
                sx={{ display: { xs: 'none', md: 'flex' }, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.06)' } }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>

              {/* Cart */}
              <IconButton color="inherit" size="small" component={RouterLink} to="/cart" sx={{ transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.06)' } }}>
                <Badge badgeContent={state.itemCount} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                  <CartIcon fontSize="small" />
                </Badge>
              </IconButton>

              {/* Favorites */}
              <IconButton color="inherit" size="small" component={RouterLink} to="/favorites" sx={{ transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.06)' } }}>
                <Badge badgeContent={favoritesState.itemCount} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                  <FavoriteIcon fontSize="small" />
                </Badge>
              </IconButton>

              {/* User Menu */}
              {user ? (
                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Menu utilisateur">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', color: 'white', width: 28, height: 28 }}>
                        {user.firstName && user.lastName 
                          ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                          : user.firstName 
                            ? user.firstName.charAt(0).toUpperCase()
                            : <PersonIcon />
                        }
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem onClick={handleCloseUserMenu}>
                      <Typography textAlign="center">
                        Bonjour, {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName || user.email}
                      </Typography>
                    </MenuItem>
                    <MenuItem onClick={handleCloseUserMenu} component={RouterLink} to="/profile">
                      <ListItemIcon>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography textAlign="center">Profil</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleCloseUserMenu} component={RouterLink} to="/favorites">
                      <ListItemIcon>
                        <FavoriteIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography textAlign="center">Mes Favoris ({favoritesState.itemCount})</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleCloseUserMenu} component={RouterLink} to="/settings">
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography textAlign="center">Paramètres</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleSignOut}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography textAlign="center">Déconnexion</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Button
                  component={RouterLink}
                  to="/signin"
                  variant="outlined"
                  size="small"
                  sx={{ 
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    px: { xs: 1, sm: 1.25 },
                    py: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: '0.75rem', sm: '0.825rem' },
                    minWidth: { xs: 'auto', md: '100px' },
                    whiteSpace: 'nowrap',
                    '&:hover': { 
                      backgroundColor: 'primary.main', 
                      color: 'white',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  {isMobile ? 'Connexion' : 'Connexion'}
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          py: 6,
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 4 }}>
            {/* Company Info */}
            <Box>
              <Logo size="medium" showTagline={true} />
              <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'text.secondary' }}>
                {t.footer.companyDescription}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ color: 'text.primary' }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton size="small" sx={{ color: 'text.primary' }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton size="small" sx={{ color: 'text.primary' }}>
                  <InstagramIcon />
                </IconButton>
                <IconButton size="small" sx={{ color: 'text.primary' }}>
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Quick Links */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                {t.footer.quickLinks}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pages.map((page) => (
                  <Button
                    key={page.name}
                    component={RouterLink}
                    to={page.path}
                    sx={{ 
                      color: 'text.secondary', 
                      justifyContent: 'flex-start',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Categories */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                {t.footer.categories}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    component={RouterLink}
                    to={`/products?category=${category.id}`}
                    sx={{ 
                      color: 'text.secondary', 
                      justifyContent: 'flex-start',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    {category.icon} {category.nameFr}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                {t.footer.contactUs}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>+221 77 123 4567</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>info@totalbat.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Dakar, Sénégal</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'divider' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t.footer.copyright.replace('{year}', new Date().getFullYear().toString())}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button size="small" sx={{ color: 'text.secondary' }}>
                {t.footer.privacyPolicy}
              </Button>
              <Button size="small" sx={{ color: 'text.secondary' }}>
                {t.footer.termsOfService}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout; 